import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AnalyticsService,
  GoogleAnalyticsService,
  NotificationService,
  RouterExtService,
} from '@app/services/_index';
import { HasPageTitleAndMetaData, MaintenanceInterface } from '@app/interfaces/_index';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';

import { MaintenanceAdapter } from '@app/adapters/_index';
import { filter, Subscription } from 'rxjs';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { GenesysAuthService } from './services/genesysAuthService.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy, HasPageTitleAndMetaData {
  isLoading: boolean;
  showHead: boolean;

  url: string; // Keep track of current url
  isDowntime = false; // Indicates if beyond is down for maintenance. This is toggled by CMS
  subs: Subscription[] = [];

  constructor(
    private router: Router,
    private _routerExtension: RouterExtService, // needed to initialize saving the current and prev endpoints
    private titleService: Title,
    public metaService: Meta,
    private notificationService: NotificationService,
    private maintenanceAdapter: MaintenanceAdapter,
    private analyticsService: AnalyticsService,
    public googleAnalyticsService: GoogleAnalyticsService,
    private translate: TranslateService,
    private genesysAuthService: GenesysAuthService
  ) {
    this.isLoading = false;
    this.setTitleAndMetaData();
    this.disableRobotsMetaTag();
    translate.setDefaultLang('en');
    translate.use('en');
  }

  /**
   * Main app onInit
   */
  ngOnInit(): void {
    // Check if the Downtime is true or false and route to page accordingly
    this.isMaintenanceDowntimeCheck();
    this.analyticsService.initialize();
    this.genesysAuthService.init();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Check if we're NOT on the voucher-redemption page
        if (!event.url.includes('/voucher-redemption')) {
          // Reset to English for all other pages
          this.translate.use('en');
        }
        // If we are on voucher-redemption, don't change the language,
        // letting the page's language selector work as intended
      });

    // TODO: This is being set in the user service by the user subject when the user signs in
    // this.subs.push(
    //   this.authService.currentUser
    //     .pipe(
    //       tap((currentUser) => {
    //         // TODO: WE don't have the kepler-sfdfc call like GKN to set the user's SFDC Account.Name and Contact.Id here.
    //         // Either that, we need to call Beyond's User. The user can be set in user.service.ts set user.
    //         this.analyticsService.updateUser(currentUser.email);
    //       })
    //     )
    //     .subscribe()
    // );
  }

  /**
   * Main app AfterViewInit
   * Calls the Google Analytics Page view
   */
  ngAfterViewInit() {
    this.initGoogleAnalyticsPageView();
  }

  /**
   * Google Analytics Page views
   */
  initGoogleAnalyticsPageView() {
    this.subs.push(
      this.router.events.subscribe((evt) => {
        if (evt instanceof NavigationEnd) {
          this.googleAnalyticsService.sendPageView(evt.urlAfterRedirects);
        }
        // This is needed to make sure the window scroll always stays at the top when navigate from one page to other.
        window.scrollTo(0, 0);
      })
    );
  }

  /**
   * Check if the Downtime is true or false and route to page accordingly
   */
  isMaintenanceDowntimeCheck(): void {
    this.subs.push(
      this.notificationService.getMaintenance().subscribe({
        next: (response: MaintenanceInterface) => {
          if (response) {
            // TODO: Refactor this in Angular 16 upgrade
            const maintenance = this.maintenanceAdapter.adapt(response[0]);
            this.isDowntime = maintenance?.enable_downtime;
            if (this.isDowntime) {
              this.router.navigate(['../maintenance']);
            }
          }
        },
        error: (error) => {
          console.log('Error: Could not load maintenance', error);
        },
      })
    );
  }

  /**
   * Set selected subscription title and metadata tags
   */
  setTitleAndMetaData(): void {
    // Set title for default main page
    this.titleService.setTitle('Education');
    this.metaService.updateTag({
      name: 'description',
      content:
        'Learn new technologies and the necessary business skills to grow in the world of customer experience, including artificial intelligence, workforce management and more.',
    });
  }

  /**
   * Disable robot crawlers in development (non-production) environements
   */
  disableRobotsMetaTag(): void {
    if (environment && !environment.production) {
      this.metaService.addTag({
        name: 'robots',
        content: 'noindex',
      });
    }
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
